import React, { useRef, useState, useEffect } from "react";
import { adminApi, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Music, Play, Pause, Image as ImageIcon, Film, Eye, EyeOff, Trash2 } from "lucide-react";
import { PREVIEW_SECONDS, PREVIEW_LABEL } from "@/context/PlayerContext";

export const panel = { backgroundColor: "var(--ink-raised)", border: "var(--rule)", borderRadius: "var(--radius)" };
const inputStyle = { border: "var(--rule)", borderRadius: "var(--radius)", color: "var(--parchment)", background: "transparent", width: "100%" };
const inputCls = "px-3 py-2.5 outline-none focus:border-[var(--gold)]";

export const Field = ({ label, help, children }) => (
  <label className="block">
    <span className="font-caps text-ash block mb-2" style={{ fontSize: ".58rem" }}>{label}</span>
    {children}
    {help && <span className="block mt-1" style={{ color: "var(--ash)", fontSize: ".72rem" }}>{help}</span>}
  </label>
);

export const Text = (props) => <input {...props} className={inputCls} style={inputStyle} />;
export const Area = (props) => <textarea {...props} className={inputCls} style={{ ...inputStyle, resize: "vertical" }} />;
export const Select = ({ children, ...p }) => <select {...p} className={inputCls} style={inputStyle}>{children}</select>;

// PasswordInput — text field with a show/hide eye toggle. Drop-in replacement for <Text type="password" ...>
export const PasswordInput = ({ testidToggle, ...props }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        {...props}
        type={visible ? "text" : "password"}
        className={inputCls}
        style={{ ...inputStyle, paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        data-testid={testidToggle || "toggle-password-visibility"}
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          padding: 6,
          cursor: "pointer",
          color: visible ? "var(--gold)" : "var(--ash)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

export const Toggle = ({ checked, onChange, label, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className="flex items-center gap-3">
    <span style={{ width: 42, height: 24, borderRadius: 12, background: checked ? "var(--gold)" : "rgba(200,155,60,.2)", position: "relative", transition: "background .2s", display: "inline-block" }}>
      <span style={{ position: "absolute", top: 2, left: checked ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "var(--ink)", transition: "left .2s" }} />
    </span>
    <span className="font-caps" style={{ fontSize: ".6rem", color: "var(--parchment)" }}>{label}</span>
  </button>
);

export const StatusPill = ({ status }) => {
  const map = { published: "var(--gold)", scheduled: "var(--gold-lift)", draft: "var(--ash)" };
  const c = map[status] || "var(--ash)";
  return <span className="font-caps" style={{ fontSize: ".5rem", color: c, border: `1px solid ${c}`, padding: ".2rem .5rem", borderRadius: "var(--radius)" }}>{status || "draft"}</span>;
};

export const Btn = ({ children, variant = "gold", ...p }) => (
  <button {...p} className={variant === "gold" ? "btn-gold" : "btn-ghost"} style={{ padding: ".7rem 1.2rem", ...(p.style || {}) }}>{children}</button>
);

// ---------------------------------------------------------------- Upload progress
// phase: 'idle' | 'uploading' | 'processing' | 'done'
export const UploadProgress = ({ phase, pct, label, testid = "upload-progress" }) => {
  if (phase === "idle") return null;
  const isUploading = phase === "uploading";
  const isProcessing = phase === "processing";
  const isDone = phase === "done";
  const width = isDone ? 100 : isProcessing ? 100 : Math.max(2, Math.min(100, pct || 0));
  const barColor = isDone ? "var(--gold)" : isProcessing ? "var(--gold-lift, var(--gold))" : "var(--gold)";
  const msg = label || (isUploading
    ? `Uploading ${Math.round(pct || 0)}%`
    : isProcessing
      ? "Uploaded — processing on server (ffmpeg)…"
      : "Done");
  return (
    <div className="mt-2" data-testid={testid} aria-live="polite">
      <div style={{
        height: 6, width: "100%", borderRadius: 999,
        background: "rgba(200,155,60,.15)", overflow: "hidden", position: "relative"
      }}>
        <div style={{
          height: "100%", width: `${width}%`, background: barColor,
          borderRadius: 999, transition: "width .25s ease",
          backgroundImage: isProcessing
            ? "linear-gradient(90deg, rgba(255,255,255,.0) 0%, rgba(255,255,255,.35) 50%, rgba(255,255,255,.0) 100%)"
            : "none",
          backgroundSize: isProcessing ? "200% 100%" : "auto",
          animation: isProcessing ? "hns-shimmer 1.1s linear infinite" : "none"
        }} />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="font-caps" style={{ fontSize: ".55rem", color: isDone ? "var(--gold)" : "var(--ash)", letterSpacing: ".08em" }}>{msg}</span>
        {isDone && <span className="font-caps" style={{ fontSize: ".55rem", color: "var(--gold)" }}>✓ ready</span>}
      </div>
      <style>{`@keyframes hns-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  );
};

// ---------------------------------------------------------------- Image uploader
export const ImageUploader = ({ value, onChange, square = true, label = "Cover artwork", help, onRemove, testid = "image-input", placeholder, valueCaption, fallbackCaption }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [pct, setPct] = useState(0);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("square", square);
    fd.append("alt", file.name);
    setBusy(true);
    setPhase("uploading");
    setPct(0);
    try {
      const { data } = await adminApi.post("/admin/media/image", fd, {
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setPct(p);
          if (p >= 100) setPhase("processing");
        },
      });
      setPhase("done");
      onChange(data.url, data);
      if (data.warning) toast.warning(data.warning);
      else toast.success("Image uploaded");
      setTimeout(() => { setPhase("idle"); setPct(0); }, 1400);
    } catch (e) {
      setPhase("idle"); setPct(0);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  const showingFallback = !value && !!placeholder;
  return (
    <Field label={label} help={help}>
      <div className="flex items-center gap-4">
        <div className="grid place-items-center overflow-hidden" style={{ width: 96, height: 96, ...panel }} data-testid={`${testid}-preview`} data-state={value ? "custom" : showingFallback ? "fallback" : "empty"}>
          {value || placeholder ? <img src={mediaUrl(value || placeholder)} alt="" className="w-full h-full object-cover" style={value ? undefined : { opacity: .45, filter: "grayscale(.25)" }} /> : <ImageIcon size={22} color="var(--ash)" />}
        </div>
        <div className="flex-1 min-w-0">
          <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={testid} />
          <div className="flex flex-wrap gap-2">
            <Btn type="button" variant="ghost" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`}><UploadCloud size={14} /> {busy ? (phase === "uploading" ? `Uploading ${pct}%` : "Processing…") : value ? "Replace image" : "Upload image"}</Btn>
            {onRemove && value && <Btn type="button" variant="ghost" onClick={onRemove} disabled={busy} data-testid={`${testid}-remove`}><Trash2 size={13} /> Remove</Btn>}
          </div>
          {value && valueCaption && <p className="mt-2 font-caps" style={{ color: "var(--gold)", fontSize: ".55rem" }} data-testid={`${testid}-caption`}>{valueCaption}</p>}
          {showingFallback && fallbackCaption && <p className="mt-2 font-caps" style={{ color: "var(--ash)", fontSize: ".55rem" }} data-testid={`${testid}-caption`}>{fallbackCaption}</p>}
          <UploadProgress phase={phase} pct={pct} testid={`${testid}-progress`} />
        </div>
      </div>
    </Field>
  );
};

// ---------------------------------------------------------------- Audio uploader + trimmer
export const AudioUploader = ({ media, onChange }) => {
  const ref = useRef();
  const audioRef = useRef();
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState(media?.preview_start || 0);
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [pct, setPct] = useState(0);
  const [fileMeta, setFileMeta] = useState(null); // { name, sizeMB }

  useEffect(() => { setStart(media?.preview_start || 0); }, [media?.id]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) {
      toast.error(`That file is ${(file.size / (1024 * 1024)).toFixed(1)} MB — max is 150 MB. Try exporting a smaller WAV or an MP3/M4A.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setPhase("uploading");
    setPct(0);
    setFileMeta({ name: file.name, sizeMB: (file.size / (1024 * 1024)).toFixed(1) });
    try {
      const { data } = await adminApi.post("/admin/media/audio", fd, {
        timeout: 600000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setPct(p);
          if (p >= 100) setPhase("processing");
        },
      });
      setPhase("done");
      onChange(data);
      setStart(data.preview_start);
      toast.success(`Audio processed — ${PREVIEW_LABEL} preview generated`);
      setTimeout(() => { setPhase("idle"); setPct(0); setFileMeta(null); }, 1800);
    } catch (e) {
      setPhase("idle"); setPct(0); setFileMeta(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };

  const retrim = async () => {
    if (!media?.id) return;
    const fd = new FormData();
    fd.append("preview_start", start);
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/media/retrim/${media.id}`, fd);
      onChange(data);
      toast.success("Preview clip updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <Field label={`Full song (master) — a ${PREVIEW_LABEL} preview from the start is generated automatically`} help={`Accepted: mp3, wav, m4a, aac, flac. Max 150 MB. The master stays private; only the ${PREVIEW_LABEL} preview is public.`}>
      <input ref={ref} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.flac" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="audio-input" />
      {!media ? (
        <>
          <Btn type="button" variant="ghost" onClick={() => ref.current?.click()} disabled={busy}><Music size={14} /> {busy ? (phase === "uploading" ? `Uploading ${pct}%` : phase === "processing" ? "Processing on server…" : "Working…") : "Upload full song"}</Btn>
          {fileMeta && phase !== "idle" && (
            <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".72rem" }}>{fileMeta.name} · {fileMeta.sizeMB} MB</p>
          )}
          <UploadProgress phase={phase} pct={pct} testid="audio-upload-progress" />
        </>
      ) : (
        <div className="p-4" style={panel}>
          <div className="flex items-center gap-3">
            <button type="button" onClick={togglePlay} className="h-10 w-10 grid place-items-center shrink-0" style={{ border: "1px solid var(--gold)", borderRadius: "50%", color: "var(--gold)" }} aria-label="Preview clip">
              {playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}
            </button>
            <div className="min-w-0">
              <p className="truncate" style={{ color: "var(--parchment)", fontSize: ".85rem" }}>{media.original_filename}</p>
              <p style={{ color: "var(--ash)", fontSize: ".72rem" }}>{Math.round(media.duration)}s track · preview from {Math.round(media.preview_start)}s · {media.peaks?.length || 0} peaks</p>
            </div>
            <Btn type="button" variant="ghost" style={{ marginLeft: "auto" }} onClick={() => ref.current?.click()}>Replace</Btn>
          </div>
          <audio ref={audioRef} src={mediaUrl(media.url)} preload="none" onEnded={() => setPlaying(false)} />
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-caps text-ash" style={{ fontSize: ".55rem" }}>Preview starts at {Math.round(start)}s</span>
              <button type="button" onClick={retrim} className="font-caps text-gold" style={{ fontSize: ".55rem", background: "none", border: "none", cursor: "pointer" }} data-testid="retrim-btn">Preview this clip →</button>
            </div>
            <input type="range" min={0} max={Math.max(0, Math.floor((media.duration || PREVIEW_SECONDS) - PREVIEW_SECONDS))} value={start} onChange={(e) => setStart(Number(e.target.value))} className="w-full" data-testid="trim-slider" />
          </div>
          {phase !== "idle" && (
            <div className="mt-3">
              {fileMeta && (
                <p style={{ color: "var(--ash)", fontSize: ".72rem" }}>Replacing with {fileMeta.name} · {fileMeta.sizeMB} MB</p>
              )}
              <UploadProgress phase={phase} pct={pct} testid="audio-upload-progress" />
            </div>
          )}
        </div>
      )}
    </Field>
  );
};

// ---------------------------------------------------------------- Video uploader
export const fmtClock = (secs) => {
  const s = Math.round(Number(secs) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
export const fmtMB = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : "");

export const VideoUploader = ({ media, onChange, onRemove, label = "Upload a video file", help = "mp4 or webm, max 500 MB.", testid = "video-input" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [pct, setPct] = useState(0);
  const [fileMeta, setFileMeta] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setPhase("uploading");
    setPct(0);
    setFileMeta({ name: file.name, sizeMB: (file.size / (1024 * 1024)).toFixed(1) });
    try {
      const { data } = await adminApi.post("/admin/media/video", fd, {
        timeout: 900000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setPct(p);
          if (p >= 100) setPhase("processing");
        },
      });
      setPhase("done");
      onChange(data);
      toast.success("Video uploaded");
      setTimeout(() => { setPhase("idle"); setPct(0); setFileMeta(null); }, 1800);
    } catch (e) {
      setPhase("idle"); setPct(0); setFileMeta(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  const meta = media ? [fmtMB(media.size), media.duration ? fmtClock(media.duration) : null, media.aspect].filter(Boolean).join(" · ") : "";
  return (
    <Field label={label} help={help}>
      <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.mov,.webm" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={testid} />
      {media ? (
        <div className="p-3 flex items-center gap-3 flex-wrap" style={panel} data-testid={`${testid}-current`}>
          {media.poster_url ? <img src={mediaUrl(media.poster_url)} alt="" style={{ width: 80, height: 45, objectFit: "cover" }} /> : <Film size={20} color="var(--ash)" />}
          <span className="min-w-0 flex-1" style={{ color: "var(--parchment)", fontSize: ".82rem" }}>
            <span className="block truncate">{media.original_filename || "uploaded video"}</span>
            {meta && <span className="block" style={{ color: "var(--ash)", fontSize: ".7rem" }} data-testid={`${testid}-meta`}>{meta}</span>}
          </span>
          <div className="flex gap-2">
            <Btn type="button" variant="ghost" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-replace`}>{busy ? `Uploading ${pct}%` : "Replace"}</Btn>
            {onRemove && <Btn type="button" variant="ghost" onClick={onRemove} disabled={busy} data-testid={`${testid}-remove`}><Trash2 size={13} /> Remove</Btn>}
          </div>
        </div>
      ) : (
        <Btn type="button" variant="ghost" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`}><UploadCloud size={14} /> {busy ? (phase === "uploading" ? `Uploading ${pct}%` : phase === "processing" ? "Processing on server…" : "Working…") : "Upload video"}</Btn>
      )}
      {fileMeta && phase !== "idle" && (
        <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".72rem" }}>{fileMeta.name} · {fileMeta.sizeMB} MB</p>
      )}
      <UploadProgress phase={phase} pct={pct} testid={`${testid}-progress`} />
    </Field>
  );
};
